import { useCallback, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { Op, ProjectDTO, StepDTO } from '@pace/contracts';
import { viewOf } from '@pace/contracts';
import { grantingRole, nextStepActed, validateDone } from '@pace/domain';
import { useLookups, type Lookups } from '../data/bootstrap';
import { useI18n } from '../i18n';
import { uuid } from '../lib/storage';
import { fmtDay, now } from '../lib/time';
import { outbox, type QueuedOp } from '../offline/outbox';
import { snapshotOf } from '../offline/optimistic';
import { useAuthed } from '../session';
import { useToast } from './Toasts';

type OpBody = Op extends infer O ? (O extends Op ? Omit<O, 'opId' | 'at'> : never) : never;

/** Queue an op for the signed-in user; returns the full op. */
export function useEnqueue() {
  const { user } = useAuthed();
  return useCallback(
    (body: OpBody, prev?: QueuedOp['prev']): Op => {
      const op = { ...body, opId: uuid(), at: now() } as Op;
      outbox.enqueue({ op, userId: user.id, ...(prev ? { prev } : {}) });
      return op;
    },
    [user.id],
  );
}

export type Access = 'any' | 'own' | 'none';

/**
 * Can the signed-in person update this step? Planners: any step. Stage owners: steps of
 * their departments, and a finished one only until the next step has acted.
 */
export function stepAccess(
  lk: Lookups,
  userId: string,
  roles: readonly string[],
  project: ProjectDTO,
  step: StepDTO,
): { access: Access; locked: boolean } {
  if (project.phase !== 'active') return { access: 'none', locked: false };
  if (grantingRole(roles as never, 'step.updateAny')) return { access: 'any', locked: false };
  if (!grantingRole(roles as never, 'step.update')) return { access: 'none', locked: false };
  const dept = lk.deptOf(step);
  const mine = !!dept && !!lk.user(userId)?.departmentIds.includes(dept);
  if (!mine) return { access: 'none', locked: false };
  const locked = step.state === 'done' && nextStepActed(viewOf(project, lk.today(), lk.cal), step.id);
  return { access: 'own', locked };
}

export function useStepAccess() {
  const lk = useLookups();
  const { user } = useAuthed();
  return useCallback(
    (project: ProjectDTO, step: StepDTO) =>
      lk ? stepAccess(lk, user.id, user.roles, project, step) : { access: 'none' as Access, locked: false },
    [lk, user.id, user.roles],
  );
}

/** Floor actions on one step: every tap applies instantly and offers a 10-second Undo. */
export function useStepActions() {
  const { t, lang } = useI18n();
  const lk = useLookups();
  const toast = useToast();
  const enqueue = useEnqueue();

  const label = useCallback(
    (p: ProjectDTO, s: StepDTO) => {
      const tool = s.toolId ? (p.tools.find((x) => x.id === s.toolId)?.name ?? '') : p.partName;
      return { stage: lk?.stageName(s) ?? '', tool };
    },
    [lk],
  );

  /** Queue, then toast with Undo that restores exactly the state before the tap. */
  const run = useCallback(
    (s: StepDTO, body: OpBody & { eventId: string }, message: string) => {
      enqueue(body);
      toast({
        message,
        onUndo: () =>
          enqueue(
            { type: 'step.undo', eventIds: [body.eventId] },
            { [body.eventId]: { ...snapshotOf(s), stepId: s.id } },
          ),
      });
    },
    [enqueue, toast],
  );

  const done = useCallback(
    (p: ProjectDTO, s: StepDTO, date: string, note?: string | null): boolean => {
      if (!lk) return false;
      const err = validateDone(s, date, lk.today());
      if (err) {
        toast({ message: t(`errors.${err}`), tone: 'error' });
        return false;
      }
      const eventId = uuid();
      run(
        s,
        { type: 'step.done', stepId: s.id, eventId, date, ...(note ? { note } : {}) },
        t('work.savedDone', { ...label(p, s), date: fmtDay(date, lang) }),
      );
      return true;
    },
    [lk, t, lang, toast, run, label],
  );

  const hold = useCallback(
    (p: ProjectDTO, s: StepDTO, reasonId: string, note?: string | null) => {
      const eventId = uuid();
      run(
        s,
        { type: 'step.hold', stepId: s.id, eventId, reasonId, ...(note ? { note } : {}) },
        t('work.savedHold', { ...label(p, s), reason: lk?.reasonLabel(reasonId) ?? '' }),
      );
    },
    [t, lk, run, label],
  );

  const resume = useCallback(
    (p: ProjectDTO, s: StepDTO) => {
      const eventId = uuid();
      run(s, { type: 'step.release', stepId: s.id, eventId }, t('work.savedResume', label(p, s)));
    },
    [t, run, label],
  );

  const reopen = useCallback(
    (p: ProjectDTO, s: StepDTO) => {
      const eventId = uuid();
      run(s, { type: 'step.reopen', stepId: s.id, eventId }, t('work.savedReopen', label(p, s)));
    },
    [t, run, label],
  );

  const note = useCallback(
    (s: StepDTO, text: string) => enqueue({ type: 'step.note', stepId: s.id, eventId: uuid(), note: text }),
    [enqueue],
  );

  return { done, hold, resume, reopen, note };
}

/** Global: tell the user when the server refused a queued tap, then resync. */
export function useRejectionToasts() {
  const { t } = useI18n();
  const toast = useToast();
  const qc = useQueryClient();
  useEffect(
    () =>
      outbox.onRejected((rs) => {
        const rejectedIds = new Set(rs.map((r) => r.op.opId));
        // An undo of a tap that itself was refused needs no second message.
        const shown = rs.filter(
          (r) =>
            r.op.type !== 'step.undo' ||
            !rs.some((x) => x.op.type !== 'step.undo' && rejectedIds.has(x.op.opId)),
        );
        const first = shown[0];
        if (first) {
          const code = first.result.code ?? 'invalid';
          toast({
            message: t('errors.rejected', { reason: t(`errors.${code}`) }),
            tone: 'error',
            durationMs: 6_000,
          });
        }
        void qc.invalidateQueries({ queryKey: ['projects'] });
      }),
    [t, toast, qc],
  );
}
